import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { platformLabel } from "../lib/api";
import { formatDateDisplay } from "../lib/utils";

const conditionColors = {
  new: "bg-sky-500 text-white",
  used: "bg-rose-500 text-white",
};

export default function ProductCard({ p }) {
  const { addToCart, addToPreOrderCart, preOrderItems } = useCart();
  const isPreOrder = p.is_preorder;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPreOrder && p.stock <= 0) return;
    if (isPreOrder) {
      const result = addToPreOrderCart(p, 1);
      if (result === "blocked") {
        toast.error("You can't add another item to pre-order. First place the order for the existing pre-order item.");
        return;
      }
      toast.success(`${p.name} added to pre-order cart`, { duration: 1500 });
    } else {
      addToCart(p, 1);
      toast.success(`${p.name} added to cart`, { duration: 1500 });
    }
  };

  const blockedByOther = isPreOrder && preOrderItems.length > 0 && !preOrderItems.find((i) => i.product_id === p.id);
  const canAdd = !blockedByOther && (isPreOrder || p.stock > 0);

  return (
    <Link
      to={`/product/${p.id}`}
      className="group relative bg-navy-700 rounded-xl overflow-hidden neon-border hover:-translate-y-1 transition-all duration-300"
      data-testid="product-card"
    >
      <div className="relative bg-navy-800 overflow-hidden" style={{ aspectRatio: "3/4" }}>
        {p.images?.[0] ? (
          <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-600 text-xs">No image</div>
        )}
        <span className={`absolute top-3 left-3 ${isPreOrder ? "bg-gold text-navy-900" : conditionColors[p.condition]} text-[10px] font-black uppercase tracking-[0.15em] px-2 py-1 rounded`}>
          {isPreOrder ? "PRE-ORDER" : p.condition === "new" ? "NEW" : "USED"}
        </span>
        {p.sony_authorised && (
          <span className="absolute top-3 right-3 bg-navy-900/80 backdrop-blur text-gold text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Sony
          </span>
        )}

        {/* Quick Add to Cart overlay button */}
        <button
          onClick={handleAddToCart}
          disabled={!canAdd}
          data-testid="quick-add-to-cart"
          className="absolute bottom-0 inset-x-0 bg-gold text-navy-900 text-[10px] font-black uppercase tracking-[0.15em] py-2 flex items-center justify-center gap-1.5 translate-y-full group-hover:translate-y-0 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShoppingBag className="w-3 h-3" />
          {isPreOrder
            ? blockedByOther ? "Pre-Order Cart Full" : "Pre-Book"
            : canAdd ? "Add to Cart" : "Out of Stock"}
        </button>
      </div>
      <div className="px-2 py-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gold/80 mb-1">{platformLabel(p.platform)}</div>
        <h3 className="font-bold text-sm line-clamp-2 min-h-[2.5rem] group-hover:text-gold transition">{p.name}</h3>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-lg font-black text-gold">₹{p.price.toLocaleString()}</span>
          {p.original_price && p.original_price > p.price && (
            <span className="text-xs text-slate-500 line-through">₹{p.original_price.toLocaleString()}</span>
          )}
        </div>
        {isPreOrder && p.release_date && (
          <div className="mt-2 text-[10px] uppercase tracking-wider text-gold/80">Releases {formatDateDisplay(p.release_date)}</div>
        )}
      </div>
    </Link>
  );
}
